import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import { useMutation } from "@apollo/react-hooks";
import { Box, Card } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useStyles from "./my-leave-my-leave-request-page.style";
import ArrowIcon from "@iconify/icons-mdi/arrow-right-bold";
import { InlineIcon } from "@iconify/react";
import { useMyHrEmployee } from "my-hr/hooks";
import { MyLeaveConfirmDialog } from "my-leave/components";
import { MY_LEAVE_DELETE_REQUEST } from "my-leave/graphql";
import { useMyLeaveRequest } from "my-leave/hooks";
import {
  OcCalendarDay,
  OcCardButtonBar,
  OcPageNameTitle,
  OcPageTransition,
  OcRequestsMessagesCard,
  OcStateChip,
} from "oc/components";
import { useOcClientState } from "oc/hooks";
import { useOcIsMobile } from "oc/hooks/use-is-mobile";
import { getFullname } from "oc/utils";

export const MyLeaveMyLeaveRequestPage = () => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  let { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const isMobile = useOcIsMobile();
  const classes = useStyles({ isMobile });

  const [newMessageText] = useOcClientState("newMessageText", "");

  const { leaveRequest, leaveRequestLoading } = useMyLeaveRequest({
    _id: id,
  });

  const { data: approvedCode } = useMyHrEmployee({
    employeeId: leaveRequest?.approvedBy,
  });
  const { data: rejectedCode } = useMyHrEmployee({
    employeeId: leaveRequest?.rejectedBy,
  });

  const [removeRequest] = useMutation(MY_LEAVE_DELETE_REQUEST);

  let messages = leaveRequest?.messages
    ? _.cloneDeep(leaveRequest.messages)
    : [];

  const [showConfirmDialog, setShowConfirmDialog] = useOcClientState<boolean>(
    "confirmDialogState",
    false,
  );
  const handleOpenConfirmDialog = () => {
    setShowConfirmDialog(true);
  };
  const handleCloseConfirmDialog = () => {
    setShowConfirmDialog(false);
  };

  const handleRemoveRequest = async () => {
    let variables: { _id: string; locale: string; message?: string } = {
      _id: id,
      locale: i18n.language,
    };
    if (newMessageText) {
      variables.message = newMessageText;
    }

    await removeRequest({ variables });
    handleClose();
  };

  const handleClose = () => {
    history.push("/my-leave/my-requests");
  };

  let loading = leaveRequestLoading && !leaveRequest;

  const chipLabel = loading ? "" : t(
    `MY_LEAVE_${leaveRequest?.state.toUpperCase()}`,
  )

  const skippableLeaves = ["recorded", "cancelled"];
  const showNewMessageBox = !skippableLeaves.includes(leaveRequest?.state);

  const getCardTitle = ({ leaveRequest }: { leaveRequest: any }) => {
    if (
      leaveRequest?.type === "leave" ||
      leaveRequest?.leaveType === "A" ||
      leaveRequest?.leaveType === "N"
    ) {
      return t("MY_LEAVE_NEW_LEAVE_BASE");
    }
    if (
      leaveRequest?.leaveType === "O" ||
      leaveRequest?.leaveType === "J" ||
      leaveRequest?.leaveType === "I"
    ) {
      return t("MY_LEAVE_ABSENCE_WORK");
    }
    if (leaveRequest?.type === "absence") {
      return t("OC_ABSENCE");
    }
  };

  return (
    <OcPageTransition>
      <OcPageNameTitle title={t("OC_REQUESTS_DETAILS")} goBack={true} />
      <Box>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="flex-start"
          alignItems="center"
          padding={isMobile ? "16px 0px 0px 0px" : "16px"}
        >
          <Box width={isMobile ? "100%" : "600px"}>
            <Card elevation={0} style={{ borderRadius: "8px" }}>
              <Box
                className={classes.title}
                id="show-leave-request-dialog-title"
              >
                {getCardTitle({ leaveRequest })}
              </Box>
              <Box className={classes.divider} />
              <Box padding="8px 24px">
                <Box className={classes.requestData}>
                  <Box
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: isMobile
                        ? "space-evenly"
                        : "space-between",
                      alignItems: "center",
                    }}
                  >
                    <OcCalendarDay
                      date={leaveRequest?.startDate}
                      loading={loading}
                      unPaidDate={false}
                    />
                    <Box className={classes.arrowContainer}>
                      <InlineIcon
                        icon={ArrowIcon}
                        color={
                          loading
                            ? theme.palette.gray30.main
                            : theme.palette.gray60.main
                        }
                      />
                    </Box>
                    <OcCalendarDay
                      date={leaveRequest?.endDate}
                      loading={loading}
                      unPaidDate={false}
                    />
                  </Box>
                  {isMobile && <Box height="24px" />}
                  <Box
                    display="flex"
                    alignItems="center"
                    width="100%"
                    justifyContent="space-around"
                  >
                    <Box className={classes.leaveTypeContainer}>
                      <Box>
                        <span
                          className={
                            loading
                              ? classes.leaveTypeTextLoading
                              : classes.leaveTypeText
                          }
                        >
                          {t(`MY_LEAVE_${leaveRequest?.leaveType}`)}
                        </span>
                      </Box>
                      <Box>
                        <span
                          className={
                            loading
                              ? classes.workDaysTextLoading
                              : classes.workDaysText
                          }
                        >
                          {leaveRequest?.effectiveWorkDays.toLocaleString()}{" "}
                          {t("OC_WORKDAYS")}
                        </span>
                      </Box>
                    </Box>
                    <Box className={classes.rightContainer}>
                      <Box className={classes.stateContainer}>
                        <Box
                          className={
                            loading
                              ? classes.createdAtTextLoading
                              : classes.createdAtText
                          }
                        >
                          {new Date(
                            leaveRequest?.createdAt,
                          ).toLocaleDateString()}
                        </Box>
                        {(leaveRequest?.state === "approved" ||
                          leaveRequest?.state === "assigned") && (
                            <Box
                              fontSize="9px"
                              paddingTop="1px"
                              style={{ color: "#919699" }}
                            >
                              {getFullname(approvedCode)}
                            </Box>
                          )}
                        {leaveRequest?.state === "rejected" && (
                          <Box
                            fontSize="9px"
                            paddingTop="1px"
                            style={{ color: "#919699" }}
                          >
                            {getFullname(rejectedCode)}
                          </Box>
                        )}
                        <Box height="2px" />
                        <OcStateChip
                          label={chipLabel}
                          state={leaveRequest?.state}
                          loading={loading}
                          variant="body2"
                          tooltip={false}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
                {showNewMessageBox && (
                  <OcRequestsMessagesCard
                    messages={messages}
                    module="MyLeave"
                    loading={loading}
                    data={leaveRequest}
                    role={
                      leaveRequest?.state === "assigned"
                        ? "assigner"
                        : "examiner"
                    }
                    disabled={loading}
                  />
                )}
                <OcCardButtonBar
                  leftButtonText={t("OC_REMOVE_REQUEST")}
                  leftButtonColor={
                    leaveRequest?.state === "requested" ? "primary" : undefined
                  }
                  leftButtonDisabled={loading}
                  handleLeftButton={handleOpenConfirmDialog}
                  leftButtonId="my-leave-my-leave-request-page-delete-button"
                  handleRightButton={handleClose}
                  rightButtonText={t("OC_CANCEL")}
                  rightButtonColor="secondary"
                  handlePrimaryButton={() => setShowConfirmDialog(true)}
                  rightButtonId="my-leave-my-leave-request-page-cancel-button"
                />
              </Box>
              <Box height="16px" />
            </Card>
            <Box height="16px" />
          </Box>
        </Box>
        <MyLeaveConfirmDialog
          showDialog={showConfirmDialog}
          closeDialog={handleCloseConfirmDialog}
          onConfirm={handleRemoveRequest}
          title={
            leaveRequest?.type === "leave"
              ? t("MY_LEAVE_REVOKE_LEAVE_REQUEST")
              : t("MY_LEAVE_REVOKE_REMOTE_WORK_REQUEST")
          }
          text={t("OC_REVOKE_REQUEST_CONFIRM")}
          confirmLabel={t("OC_REMOVE_REQUEST")}
          cancelLabel={t("OC_CANCEL")}
          isMobile={isMobile}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveMyLeaveRequestPage;
